import React, { useMemo, useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ENTITY_TYPES, getTypeLabel } from '@/constants/entityTypes';
import {
  Search,
  Plus,
  Upload,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Eye,
  MapPin,
  Square,
  FileText,
  FileSpreadsheet,
  Users,
  Star,
  Building2,
  Layers,
  Camera,
} from 'lucide-react';

// A synthetic key for entities that don't belong to any business_group.
// Kept out of the sort so ungrouped rows always sink to the bottom.
const UNGROUPED = '__ungrouped__';

const EntitiesTab = ({
  // data
  entities,
  loading,
  stats,
  user,
  pagination,
  setPagination,
  // search/filter
  searchQuery,
  setSearchQuery,
  filterType,
  setFilterType,
  filterVerified,
  setFilterVerified,
  // selection
  selectedEntityIds,
  setSelectedEntityIds,
  toggleEntitySelection,
  toggleSelectAllEntities,
  bulkDeleting,
  // backfill
  backfilling,
  backfillProgress,
  runBackfillLoop,
  stopBackfill,
  handleBackfillCoords,
  // actions
  fetchEntities,
  handleExportEntities,
  setShowBulkUploadModal,
  resetEntityForm,
  setShowEntityModal,
  handleBulkDelete,
  handleToggleFeatured,
  handleBulkSetFeatured,
  openEntityAdminsModal,
  navigate,
  openEditModal,
  handleVerifyEntity,
  handleDeleteEntity,
  handlePrewarmScreenshots,
  currentUser,
}) => {
  const isSuperAdmin = currentUser?.admin_role === 'super_admin';
  // Group-by-Organization toggle. When on, we bucket rows by `business_group`
  // and render each bucket under a collapsible parent row; when off, we show
  // a plain table with an "Organization" column so admins can still sort/scan.
  const [groupByOrg, setGroupByOrg] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  const toggleGroupCollapsed = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Buckets rendered when groupByOrg=true. Sorted alphabetically by group name;
  // ungrouped rows appear last under their own "No organization" header.
  const groupedBuckets = useMemo(() => {
    const map = new Map();
    for (const e of entities) {
      const key = (e.business_group || '').trim() || UNGROUPED;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    const named = [];
    let ungrouped = [];
    for (const [key, list] of map) {
      if (key === UNGROUPED) ungrouped = list;
      else named.push([key, list]);
    }
    named.sort(([a], [b]) => a.localeCompare(b));
    return ungrouped.length ? [...named, [UNGROUPED, ungrouped]] : named;
  }, [entities]);

  const totalColSpan = 9; // checkbox + 7 data columns + actions
  return (
    <TabsContent value="entities" className="space-y-6">
      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex flex-1 flex-wrap gap-3 w-full md:w-auto">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search entities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchEntities()}
              className="pl-9"
              data-testid="entity-search-input"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {ENTITY_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterVerified} onValueChange={setFilterVerified}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-entities-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Export entities</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleExportEntities('csv')}
                data-testid="export-entities-csv"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Download as CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportEntities('pdf')}
                data-testid="export-entities-pdf"
              >
                <FileText className="h-4 w-4 mr-2" />
                Download as PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                Respects current filters
              </p>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" onClick={() => setShowBulkUploadModal(true)} data-testid="bulk-upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
          <Button
            variant={groupByOrg ? 'default' : 'outline'}
            onClick={() => setGroupByOrg((v) => !v)}
            data-testid="group-by-org-toggle"
            title="Collapse locations that share a Business / Organization into expandable groups"
          >
            <Layers className="h-4 w-4 mr-2" />
            {groupByOrg ? 'Grouped' : 'Group by Org'}
          </Button>
          {isSuperAdmin && (
            <Button
              variant="outline"
              onClick={handlePrewarmScreenshots}
              data-testid="prewarm-screenshots-btn"
              title="Ask WordPress mshots to cache a homepage screenshot for every partner without an uploaded image. Runs in the background."
            >
              <Camera className="h-4 w-4 mr-2" />
              Prewarm Screenshots
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleBackfillCoords}
            disabled={backfilling || (stats?.entities?.geocode_pending ?? 0) === 0}
            data-testid="backfill-coords-btn"
            title="Geocode all entities missing map coordinates (Nominatim, ~1 req/sec, runs in 15-row batches)"
          >
            {backfilling ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4 mr-2" />
            )}
            {backfilling
              ? 'Geocoding…'
              : stats?.entities?.geocode_pending > 0
                ? `Geocode All (${stats.entities.geocode_pending})`
                : 'Geocode All'}
          </Button>
          <Button onClick={() => { resetEntityForm(); setShowEntityModal(true); }} data-testid="add-entity-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Entity
          </Button>
        </div>
      </div>

      {/* Geocode Pending / Live-Progress Banner */}
      {(backfillProgress || (stats?.entities?.geocode_pending ?? 0) > 0) && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3"
          data-testid="admin-geocode-pending-banner"
        >
          {backfillProgress ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {backfilling ? (
                    <RefreshCw className="h-4 w-4 text-amber-500 animate-spin shrink-0" />
                  ) : (
                    <MapPin className="h-4 w-4 text-amber-500 shrink-0" />
                  )}
                  <p className="text-sm font-medium truncate" data-testid="backfill-progress-text">
                    {backfilling ? 'Geocoding' : 'Finished geocoding'}
                    {' '}
                    <span className="tabular-nums">
                      {backfillProgress.done}/{backfillProgress.total}
                    </span>
                    {backfillProgress.failed > 0 && (
                      <span className="text-muted-foreground font-normal ml-2">
                        · {backfillProgress.failed} unresolvable
                      </span>
                    )}
                  </p>
                </div>
                {backfilling && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={stopBackfill}
                    data-testid="backfill-stop-btn"
                  >
                    <Square className="h-3 w-3 mr-1 fill-current" />
                    Stop
                  </Button>
                )}
              </div>
              <Progress
                value={
                  backfillProgress.total > 0
                    ? Math.min(100, (backfillProgress.done / backfillProgress.total) * 100)
                    : 0
                }
                className="h-2"
                data-testid="backfill-progress-bar"
              />
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {stats.entities.geocode_pending} of {stats.entities.total} entities are missing map coordinates.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  They have addresses but no lat/lng yet so they don't appear on the Discovery map.
                  Click <strong>Geocode All</strong> to drain the queue in 15-row batches; you can stop anytime.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={runBackfillLoop}
                data-testid="banner-geocode-now-btn"
              >
                Geocode All
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedEntityIds.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3"
          data-testid="bulk-actions-bar"
        >
          <p className="text-sm font-medium" data-testid="bulk-selected-count">
            {selectedEntityIds.length} {selectedEntityIds.length === 1 ? 'entity' : 'entities'} selected
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedEntityIds([])}
              data-testid="bulk-clear-btn"
            >
              Clear
            </Button>
            {user?.admin_role === 'super_admin' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkSetFeatured(true)}
                  data-testid="bulk-feature-btn"
                >
                  <Star className="h-4 w-4 mr-1.5" />
                  Feature {selectedEntityIds.length}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleBulkSetFeatured(false)}
                  data-testid="bulk-unfeature-btn"
                >
                  <Star className="h-4 w-4 mr-1.5" />
                  Unfeature
                </Button>
              </>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              {bulkDeleting
                ? 'Working...'
                : user?.admin_role === 'moderator'
                  ? `Suspend ${selectedEntityIds.length}`
                  : `Delete ${selectedEntityIds.length}`}
            </Button>
          </div>
        </div>
      )}

      {/* Entities Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={
                    entities.length > 0 &&
                    selectedEntityIds.length === entities.length
                  }
                  onCheckedChange={toggleSelectAllEntities}
                  aria-label="Select all entities on this page"
                  data-testid="select-all-entities"
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead>Partner Admins</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={totalColSpan} className="text-center py-8">
                  <div className="flex justify-center">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                </TableCell>
              </TableRow>
            ) : entities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColSpan} className="text-center py-8 text-muted-foreground">
                  No entities found
                </TableCell>
              </TableRow>
            ) : (() => {
              // The row body is identical in both modes — we just draw a
              // group-header row above each bucket when groupByOrg is on.
              const renderRow = (entity) => (
                <TableRow
                  key={entity.entity_id}
                  data-state={selectedEntityIds.includes(entity.entity_id) ? 'selected' : undefined}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedEntityIds.includes(entity.entity_id)}
                      onCheckedChange={() => toggleEntitySelection(entity.entity_id)}
                      aria-label={`Select ${entity.name}`}
                      data-testid={`select-entity-${entity.entity_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {entity.image_url && (
                        <img
                          src={entity.image_url}
                          alt={entity.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <p className="font-medium">{entity.name}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {entity.entity_id}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell data-testid={`entity-org-${entity.entity_id}`}>
                    {entity.business_group ? (
                      <Badge variant="secondary" className="whitespace-nowrap gap-1">
                        <Building2 className="h-3 w-3" />
                        {entity.business_group}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="whitespace-nowrap">
                      {getTypeLabel(entity.entity_type)}
                    </Badge>
                  </TableCell>
                  <TableCell>{entity.location || '-'}</TableCell>
                  <TableCell>
                    {entity.verified ? (
                      <Badge className="bg-green-500/20 text-green-500 border-0">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-500/20 text-amber-500 border-0">
                        <XCircle className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {entity.rating > 0 ? entity.rating.toFixed(1) : '-'}
                  </TableCell>
                  <TableCell>
                    {user?.admin_role === 'super_admin' ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleFeatured(entity)}
                        title={entity.is_featured ? 'Unfeature partner' : 'Mark as featured'}
                        data-testid={`toggle-featured-${entity.entity_id}`}
                      >
                        <Star
                          className={`h-4 w-4 ${entity.is_featured ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`}
                        />
                      </Button>
                    ) : entity.is_featured ? (
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {entity.entity_admins && entity.entity_admins.length > 0 ? (
                      <Badge variant="secondary" className="text-xs">
                        {entity.entity_admins.length} Admin{entity.entity_admins.length > 1 ? 's' : ''}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {user?.admin_role === 'super_admin' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEntityAdminsModal(entity)}
                          title="Manage Partner Admins"
                          data-testid={`manage-admins-${entity.entity_id}`}
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/entity/${entity.entity_id}`)}
                        data-testid={`view-${entity.entity_id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditModal(entity)}
                        data-testid={`edit-${entity.entity_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleVerifyEntity(entity.entity_id, !entity.verified)}
                        data-testid={`verify-${entity.entity_id}`}
                      >
                        {entity.verified ? (
                          <XCircle className="h-4 w-4 text-amber-500" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteEntity(entity.entity_id)}
                        className="text-destructive"
                        data-testid={`delete-${entity.entity_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );

              if (!groupByOrg) {
                return entities.map(renderRow);
              }

              // Grouped mode — one header row per business_group with a
              // collapse chevron and location count, followed by child rows
              // (indented via a leading empty cell to hint at hierarchy).
              return groupedBuckets.flatMap(([key, list]) => {
                const collapsed = collapsedGroups.has(key);
                const label = key === UNGROUPED ? 'No organization' : key;
                return [
                  <TableRow
                    key={`group-${key}`}
                    className="bg-muted/40 hover:bg-muted/60 cursor-pointer"
                    onClick={() => toggleGroupCollapsed(key)}
                    data-testid={`org-group-header-${key === UNGROUPED ? 'ungrouped' : key.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <TableCell colSpan={totalColSpan} className="py-2">
                      <div className="flex items-center gap-2 font-medium">
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                        />
                        {key === UNGROUPED ? (
                          <span className="text-muted-foreground italic">{label}</span>
                        ) : (
                          <>
                            <Building2 className="h-4 w-4 text-primary" />
                            <span>{label}</span>
                          </>
                        )}
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {list.length} location{list.length === 1 ? '' : 's'}
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>,
                  ...(collapsed ? [] : list.map(renderRow)),
                ];
              });
            })()}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {pagination.limit >= 10000 ? (
              <>Showing all {pagination.total}</>
            ) : (
              <>Showing {pagination.total === 0 ? 0 : pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}</>
            )}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select
              value={pagination.limit >= 10000 ? 'all' : String(pagination.limit)}
              onValueChange={(v) => {
                const next = v === 'all' ? 10000 : parseInt(v, 10);
                setPagination((prev) => ({ ...prev, limit: next, skip: 0 }));
              }}
            >
              <SelectTrigger
                className="h-8 w-[90px]"
                data-testid="entities-page-size-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50" data-testid="page-size-50">50</SelectItem>
                <SelectItem value="75" data-testid="page-size-75">75</SelectItem>
                <SelectItem value="100" data-testid="page-size-100">100</SelectItem>
                <SelectItem value="all" data-testid="page-size-all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {pagination.limit < 10000 && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.skip === 0}
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.skip + pagination.limit >= pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              data-testid="next-page-btn"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </TabsContent>
  );
};

export default EntitiesTab;
